import AwsUploadUrl from "@/interfaces/awsUploadUrl.interface";
import { Loading, Message, MessageBox } from "element-ui";
import useHttpRequest from "../use-http-request";
import { nextTick, onMounted, onUnmounted, ref, Ref, watch } from "vue";
import { DraftStorage } from "@/helpers/onlineAnswerSheet/draftStorage";
import { addSeconds } from "date-fns";
import { useUploadResourceToAWS } from "../use-upload-resource-to-aws";
import store from "@/store";
import { Editor } from "tinymce";
import Examination from "@/interfaces/examination.interface";
import axios from "axios";
import { TinymceSaveChangesError } from "@/types/tinymce-save-changes-error.type";

export const useManageStudentChanges = ({
  draftStorage,
  examination,
  textEditorInstance,
}: {
  examination: Ref<Examination | null>;
  draftStorage: Ref<DraftStorage | undefined>;
  textEditorInstance: Ref<Editor | undefined>;
}) => {
  // const UPLOAD_URL_EXPIRY_DURATION_SECS = 30; // 1 hour
  // const CLOUD_SYNC_INTERVAL_MS = 4000; // 4 seconds

  const UPLOAD_URL_EXPIRY_DURATION_SECS = 6300; // 1 hour and 45 minutes i.e 15 mins shorter than the actual upload url 2 hours
  const CLOUD_SYNC_INTERVAL_MS = 1000 * 60 * 5; // 5 minutes

  let originalOnlineAnswersHtml = "";
  let cloudSyncInterval: ReturnType<typeof setInterval> | undefined;
  let uploadUrlExpiresAtEpochTimeInSecs = 0;
  let onlineAnswerSheetUploadUrlData:
    | {
        upload_url: AwsUploadUrl;
        path_key: string;
      }
    | undefined;

  const onlineAnswersHtml = (): string => {
    return textEditorInstance.value?.getContent({ format: "html" }) || "";
  };

  const invalidateExpiredOnlineAnswerSheetUploadUrl = () => {
    if (!onlineAnswerSheetUploadUrlData) return;

    const hasUploadUrlExpired = +new Date() > uploadUrlExpiresAtEpochTimeInSecs;

    if (hasUploadUrlExpired) {
      onlineAnswerSheetUploadUrlData = undefined;
    }
  };

  // ======================================================================
  // Request Cloud Upload URL
  const requestOnlineAnswerSheetUploadUrl = async () => {
    const request = await useHttpRequest<{
      success: boolean;
      message: string;
      online_answer_sheet: { upload_url: AwsUploadUrl; path_key: string };
    }>({
      method: "POST",
      url: "examinations/request-for-online-answersheet-upload-url",
      body: {
        examination_id: examination.value?.examination_id,
      },
      loadingProperty: null,
      showSuccessMessage: false,
      errorLoadingProperty: null,
    });

    if (!request) {
      return;
    }

    if (
      request.success &&
      request.message == "ONLINE ANSWER SHEET UPLOAD URL GENERATED"
    ) {
      // Set the upload url to expire after 1 hour
      uploadUrlExpiresAtEpochTimeInSecs = +addSeconds(
        new Date(),
        UPLOAD_URL_EXPIRY_DURATION_SECS
      );
      return request.online_answer_sheet;
    }
  };

  const saveStudentChanges = async (): Promise<string | null> => {
    try {
      const content = onlineAnswersHtml();

      //Otherwise, submit the Answer Sheet
      Message.info("Syncing local changes online");

      //First attempt to save the draft locally
      const savedLocally = await draftStorage.value?.saveAsync(content);

      if (!savedLocally) {
        console.warn("[Draft] Skipped local save, relying on AWS only");
      }

      invalidateExpiredOnlineAnswerSheetUploadUrl();

      if (!onlineAnswerSheetUploadUrlData) {
        onlineAnswerSheetUploadUrlData =
          await requestOnlineAnswerSheetUploadUrl();
      }

      if (!onlineAnswerSheetUploadUrlData) return null;

      const blob = new Blob([content], { type: "text/xml" });

      const uploaded = await useUploadResourceToAWS({
        awsUploadUrl: onlineAnswerSheetUploadUrlData.upload_url,
        selectedFile: blob,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        onProgress: (progressEvent) => {
          // Do nothing for now
        },
      });

      if (uploaded) {
        Message.success("Local changes synced online");
        return content;
      }

      return null;
    } catch (error) {
      return null;
    }
  };

  const saveStudentFinalChanges = async (): Promise<boolean | null> => {
    try {
      const content = onlineAnswersHtml();
      const request = await useHttpRequest<{
        success: boolean;
        message: string;
        online_answer_sheet: { upload_url: AwsUploadUrl; path_key: string };
      }>({
        method: "POST",
        url: "examinations/request-for-online-answersheet-upload-url",
        body: {
          examination_id: examination.value?.examination_id,
          is_final: true,
        },
        loadingProperty: null,
        showSuccessMessage: false,
        errorLoadingProperty: null,
      });

      if (
        request?.success &&
        request?.message == "ONLINE ANSWER SHEET UPLOAD URL GENERATED"
      ) {
        const blob = new Blob([content], { type: "text/xml" });

        const uploaded = await useUploadResourceToAWS({
          awsUploadUrl: request.online_answer_sheet.upload_url,
          selectedFile: blob,
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          onProgress: (progressEvent) => {
            // Do nothing for now
          },
        });

        if (uploaded) {
          return true;
        }

        return false;
      }

      return false;
    } catch (error) {
      return null;
    }
  };

  // ======================================================================
  // Cloud Sync: Triggered periodically
  const periodicallySyncToCloud = () => {
    cloudSyncInterval = setInterval(async () => {
      const content = onlineAnswersHtml();
      // Only sync if content has changed relative to what was last uploaded.
      if (originalOnlineAnswersHtml !== content) {
        await saveStudentChanges();
        originalOnlineAnswersHtml = content;
      }
    }, CLOUD_SYNC_INTERVAL_MS);
  };

  const getStudentChanges = async (): Promise<boolean> => {
    let loadingIndicator: ReturnType<typeof Loading.service> | undefined;
    try {
      loadingIndicator = Loading.service({
        lock: true,
        text: "Please wait...",
        background: "rgba(0, 0, 0, 0.7)",
      });

      await nextTick();

      const localDraft = draftStorage.value?.get();
      if (localDraft) {
        textEditorInstance.value?.setContent(localDraft);
        originalOnlineAnswersHtml = onlineAnswersHtml();
        console.log("[Draft] Loaded from localStorage");
        return true;
      }

      if (examination.value?.aws_online_answersheet_path) {
        try {
          //Add a time stamp to prevent the browser from caching this request as it would cause incorrect answer sheets on next retrieval
          //since the browser would identify this url and serve a copy from cache
          let request = await axios.get(
            `${
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              examination.value!.aws_online_answersheet_path
            }?timestamp=${new Date().getTime()}`,
            {
              headers: {
                "Content-Type": "text/html",
                Authorization: " ",
              },
            }
          );

          //Update the editor instance with the new content
          textEditorInstance.value?.setContent(request.data);
          originalOnlineAnswersHtml = onlineAnswersHtml();
        } catch (error) {
          //TODO: Report this to firebase analytics
          const confirm = await MessageBox.alert(
            "We couldn't retrieve your recent changes to the online answersheet. You can either retry or start over. Retrying will help preserve your progress.",
            {
              type: "error",
              title: "Unable to Retrieve Changes",
              confirmButtonText: "Try Again",
              cancelButtonText: "Start Over",
              showCancelButton: true,
              distinguishCancelAndClose: true,
              closeOnClickModal: false,
              closeOnPressEscape: false,
            }
          );
          //If they confirm, just refresh the page
          if (confirm == "confirm") {
            window.location.reload();
          }
        }

        return true;
      }

      return false;
    } catch (error) {
      return false;
    } finally {
      if (loadingIndicator) {
        loadingIndicator.close();
      }
    }
  };

  const stop = watch(
    [examination, textEditorInstance],
    async ([exam, editor]) => {
      if (exam && editor) {
        await nextTick(); // Wait for any pending DOM updates
        await getStudentChanges();

        stop(); // Stop the watcher after the first call
      }
    },
    { immediate: true }
  );

  let failedCount = 1;
  const showEditorContentSaveErrorDialog = ref(false);
  const onChangesFailedRedirectTo: Ref<string | undefined> = ref();

  const onEditorSaveChangesFailed = (error: TinymceSaveChangesError) => {
    if (error === "Error while getting content") {
      if (failedCount < 2) {
        showEditorContentSaveErrorDialog.value = true;
        failedCount++;
      } else {
        onChangesFailedRedirectTo.value = "/dashboard/examinations";
        showEditorContentSaveErrorDialog.value = true;
      }
    }
  };

  onMounted(() => {
    periodicallySyncToCloud(); // Start cloud sync on a 5-minute interval
  });

  onUnmounted(() => {
    clearInterval(cloudSyncInterval);

    //Remove the counter when the Page is closed
    store.commit("UPDATE_ASSESSMENT_TIME_LEFT", "");
  });

  return {
    saveStudentChanges,
    saveStudentFinalChanges,
    onEditorSaveChangesFailed,
    showEditorContentSaveErrorDialog,
    onChangesFailedRedirectTo,
  };
};
